import { DragDropProvider } from '@dnd-kit/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useHandleOnDrop } from '~/hooks/step/useHandleOnDrop';
import { useForceRender } from '~/hooks/useForceRender';
import { useLiveStateManager } from '~/hooks/useLiveStateManager';
import type { SelectedStep } from '~/server/logic/prisma/step-select';
import { api } from '~/trpc-extra/api';
import {
  activeStepAtom,
  filteredStepsAtom,
  isMultitabTestAtom,
  selectedOperationIdAtom,
  selectedStepsIdsAtom,
  stepIdToScrollToAtom,
  stepSearchBarKeywordAtom,
} from '../../../atoms/no-code-atoms';
import { InstalledExtensionsStep } from './step/InstalledExtensionsStep';
import NewStepControl from './step/NewStepControl';
import Step from './step/Step';

export default function StepList({
  testContentId,
  readonly,
  className,
  // TODO: the step idx may not be correct. Should use stepId instead
}: {
  testContentId: string;
  readonly?: boolean;
  className?: string;
}) {
  const keyword = useAtomValue(stepSearchBarKeywordAtom.debouncedValueAtom);
  const setFilteredSteps = useSetAtom(filteredStepsAtom);
  const setIsMultitabTest = useSetAtom(isMultitabTestAtom);
  const [stepIdToScrollTo, setStepIdToScrollTo] = useAtom(stepIdToScrollToAtom);

  const { data: testContent } = api.testContent.get.useQuery(
    { id: testContentId },
    { refetchOnMount: false, refetchOnWindowFocus: false, enabled: !!testContentId },
  );

  const steps = useMemo(() => testContent?.steps || [], [testContent?.steps]);
  const chromeExtensions = useMemo(
    () => testContent?.chromeExtensionSelections || [],
    [testContent?.chromeExtensionSelections],
  );

  const { recorderInitiator } = useLiveStateManager();

  // Combine the steps with the component's internal steps and enrich them with metadata
  const stepsToUse: { disabled: boolean; step: SelectedStep; filteredIn: boolean }[] = useMemo(() => {
    // Enrich each step with metadata:
    // - disabled: Whether the step is part of a component's internal steps
    // - step: The original step object
    // - filteredIn: Whether the step matches the current search keyword
    //   Searches through:
    //   - Step description
    //   - Operation descriptions
    //   - Navigation operation URLs
    //   - Action operation details (action name and target name)
    //   - Code block contents
    return steps.map((step) => ({
      disabled: false,
      step,
      // Search the step's description and all its operations' internal text for the keyword
      filteredIn:
        keyword ?
          step.description.toLowerCase().includes(keyword.toLowerCase()) ||
          step.operations.some((op) => {
            const searchText = keyword.toLowerCase();
            return (
              op.navigationOp?.value?.toLowerCase().includes(searchText) ||
              op.actionOp?.action?.toLowerCase().includes(searchText) ||
              op.actionOp?.targetV2?.name?.toLowerCase().includes(searchText) ||
              op.codeBlockOp?.code?.toLowerCase().includes(searchText) ||
              op.defineVariableOp?.name?.toLowerCase().includes(searchText) ||
              op.defineVariableOp?.value?.toLowerCase().includes(searchText) ||
              op.receiveEmailOp?.toEmailAddressValue?.toLowerCase().includes(searchText) ||
              op.receiveEmailOp?.variableExtraction?.variableName?.toLowerCase().includes(searchText) ||
              op.receiveEmailOp?.variableExtraction?.extractionType?.toLowerCase().includes(searchText) ||
              op.fetchRequestOp?.url?.toLowerCase().includes(searchText) ||
              op.fetchRequestOp?.method?.toLowerCase().includes(searchText) ||
              op.fetchRequestOp?.responseVariableName?.toLowerCase().includes(searchText) ||
              op.conditionalOp?.conditionPrompt?.toLowerCase().includes(searchText)
            );
          })
        : true,
    }));
  }, [steps, keyword]);

  const selectedStepsIds = useAtomValue(selectedStepsIdsAtom);
  const [activeStep, setActiveStep] = useAtom(activeStepAtom);
  const resetActiveStep = useResetAtom(activeStepAtom);
  const resetSelectedOperationId = useResetAtom(selectedOperationIdAtom);
  const [selectedOperationId, setSelectedOperationId] = useAtom(selectedOperationIdAtom);
  const forceRender = useForceRender();

  const stepsRefs = useRef<(HTMLLIElement | null)[]>([]);

  // Keep the selected step automatically scrolled into view
  // TODO: this may not be the most efficient way to do this since each flow has to run this logic
  const numTestSteps = stepsToUse.length;

  // Set the state for other components that need to know which steps are filtered in
  useEffect(() => {
    setFilteredSteps(stepsToUse.filter((step) => step.filteredIn).map((step) => step.step));
  }, [stepsToUse, setFilteredSteps]);

  // Default to selecting the first operation in the first step
  // unless it's a 'Go to' step (hacky filter criteria)
  useEffect(() => {
    if (setSelectedOperationId && !selectedOperationId && !activeStep) {
      if (stepsToUse[0]?.step.description.startsWith('Go to')) {
        return;
      }
      setSelectedOperationId(stepsToUse[0]?.step.operations[0]?.id);
    }
  }, [setSelectedOperationId, selectedOperationId, stepsToUse, activeStep]);

  useEffect(() => {
    return () => {
      resetActiveStep();
    };
  }, [resetActiveStep]);

  useEffect(() => {
    return () => {
      resetSelectedOperationId();
    };
  }, [resetSelectedOperationId]);

  useEffect(() => {
    setIsMultitabTest(steps.some((step) => step.operations.some((op) => op.pageAlias !== 'page' || op.opensPageAlias)));
  }, [steps]);

  const { handleOnDrop, isMoving } = useHandleOnDrop({ testContentId });

  // handling inter-op and inter-step nav via arrow keys and clicking ========================
  const operationIds = useMemo(
    () => steps.map((step) => step.operations.filter((op) => op.type !== 'CODE_BLOCK').map((op) => op.id)).flat(),
    [steps],
  );

  const currentIndex = useMemo(() => {
    const idx = operationIds.findIndex((opId) => opId === selectedOperationId);
    return idx === -1 ? 0 : idx;
  }, [selectedOperationId, operationIds]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.target !== document.body) {
        return;
      }

      if (e.code === 'ArrowUp' || e.code === 'ArrowLeft') {
        setSelectedOperationId(operationIds[Math.max(currentIndex - 1, 0)]);
      } else if (e.code === 'ArrowDown' || e.code === 'ArrowRight') {
        setSelectedOperationId(operationIds[Math.min(currentIndex + 1, operationIds.length - 1)]);
      }
    },
    [currentIndex, operationIds, setSelectedOperationId],
  );

  useEffect(() => {
    // disable during recording/preview
    if (recorderInitiator?.executionType) {
      return;
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown, recorderInitiator?.executionType]);

  useEffect(() => {
    if (selectedOperationId && steps) {
      // set the active step to be the step that contains the selected operation
      const newStep = steps.find((step) => step.operations.some((op) => op.id === selectedOperationId));
      if (newStep && newStep.id !== activeStep?.id) {
        setActiveStep(newStep);
      }
    }
  }, [selectedOperationId, steps, activeStep]);

  useEffect(() => {
    if (activeStep && !selectedOperationId) {
      // set the selected operation to be the first operation in the active step
      const firstOp = activeStep.operations.find((op) => op.type != 'CODE_BLOCK');
      if (firstOp) {
        setSelectedOperationId(firstOp.id);
      }
    }
  }, [activeStep, steps, selectedOperationId]);

  useEffect(() => {
    if (stepIdToScrollTo) {
      setTimeout(() => {
        const step = document.getElementById(`step-${stepIdToScrollTo}`);

        step?.scrollIntoView({ behavior: 'smooth' });
        setStepIdToScrollTo(undefined);
      }, 500);
    }
  }, [stepIdToScrollTo]);

  const handleOperationClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>, operationId: string, step: SelectedStep) => {
      e.stopPropagation();
      setSelectedOperationId(operationId);
      setActiveStep(step);
    },
    [setActiveStep, setSelectedOperationId],
  );
  // ========================================================================================

  return (
    <DragDropProvider
      onDragEnd={handleOnDrop}
      // Force re-renders on drag start to prevent draggable items from not being able
      // to be dropped within their list (particularly OperationList)
      onDragStart={forceRender}
    >
      <div className={className}>
        <div className="h-full">
          {chromeExtensions.length > 0 && (
            <InstalledExtensionsStep
              readonly={readonly}
              testContentId={testContentId}
              extensions={chromeExtensions}
            />
          )}
          <ul className="h-full">
            {stepsToUse.map(({ disabled, step, filteredIn }, idx) => (
              <li
                key={step.id}
                ref={(el) => {
                  stepsRefs.current[idx] = el;
                }}
                className={`last:pb-6 ${filteredIn ? '' : 'hidden'}`}
              >
                {idx === 0 && (
                  <div className="z-50 mx-10 h-0">
                    <NewStepControl
                      testContentId={testContentId}
                      activePageAlias={stepsToUse[0]?.step.operations.at(0)?.pageAlias}
                      insertAfterStepIndex={-1}
                      key="top-new-step"
                      insertAfterStepOrder={-1}
                      hide={readonly}
                      disabled={readonly || isMoving}
                      nextStepId={stepsToUse.at(0)?.step.id}
                    />
                  </div>
                )}
                <Step
                  testContentId={testContentId}
                  step={step}
                  idx={idx}
                  handleOperationClick={handleOperationClick}
                  onClick={() => {
                    setActiveStep(step);
                    setSelectedOperationId(undefined);
                  }}
                  selected={selectedStepsIds.includes(step.id)}
                  readonly={readonly}
                  disabled={disabled || isMoving}
                  isFirstNavOp={idx === 0 && step.operations.length > 0 && step.operations[0].type === 'NAVIGATION'}
                />
                {idx !== stepsToUse.length - 1 && (
                  <div className="relative z-50 mx-10">
                    <NewStepControl
                      testContentId={testContentId}
                      activePageAlias={
                        stepsToUse[idx]?.step.operations.at(-1)?.opensPageAlias ||
                        stepsToUse[idx]?.step.operations.at(-1)?.pageAlias
                      }
                      insertAfterStepIndex={idx}
                      key={`${idx}-${step.id}`}
                      insertAfterStepOrder={step.stepOrder}
                      hide={readonly}
                      disabled={readonly || isMoving}
                      prevStepId={stepsToUse[idx]?.step.id}
                      nextStepId={stepsToUse.at(idx + 1)?.step.id}
                    />
                  </div>
                )}
              </li>
            ))}

            {numTestSteps === 0 &&
              (!readonly ?
                <li
                  key="generation-not-started"
                  className="relative last:pb-6"
                >
                  <NewStepControl
                    testContentId={testContentId}
                    activePageAlias={undefined}
                    insertAfterStepIndex={0}
                    key="0"
                    insertAfterStepOrder={-1}
                    hide={false}
                    isLastStep={true}
                    disabled={readonly || isMoving}
                  />
                </li>
              : <li className="flex justify-center pt-8 text-stone-700 last:pb-6 dark:text-stone-400">
                  No steps yet
                </li>)}
            {!readonly && numTestSteps !== 0 && (
              <li
                key="final-new-step"
                className="last:pb-6"
              >
                <div className="relative mx-10">
                  <NewStepControl
                    testContentId={testContentId}
                    insertAfterStepIndex={numTestSteps - 1}
                    key={`${numTestSteps - 1}-${stepsToUse.at(-1)?.step.id}`}
                    insertAfterStepOrder={stepsToUse.at(-1)?.step.stepOrder || 0}
                    hide={false}
                    disabled={readonly || isMoving}
                    isLastStep={true}
                    prevStepId={stepsToUse.at(-1)?.step.id}
                    activePageAlias={
                      stepsToUse.at(-1)?.step.operations.at(-1)?.opensPageAlias ||
                      stepsToUse.at(-1)?.step.operations.at(-1)?.pageAlias
                    }
                  />
                </div>
              </li>
            )}
          </ul>
        </div>
      </div>
    </DragDropProvider>
  );
}
